import { sortDirection } from 'consts/data';
import Timer from 'helpers/setTimer';
import { cloneDeep, isArray, isEmpty, isEqual, isPlainObject, isString } from 'lodash';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import queryString from 'query-string';
import FiltersService from 'services/FiltersService';
import { isDefine, IsJsonString } from 'helpers';
import { useQueryParam } from './useQueryParam';

const useFilters = (initialFilters, options = { fromURL: false }) => {
  const params = useQueryParam();
  const history = useHistory();
  const timerInput = useRef(new Timer());

  const { clearSortWhenDefault, fromURL } = options || {};

  const filtersFromURL = useMemo(() => {
    if (isString(params.filters) && IsJsonString(params.filters)) {
      return JSON.parse(params.filters);
    }

    return {};
  }, [params.filters]);

  const [filters, setFilters] = useState(() => {
    if (isEmpty(initialFilters)) {
      return {
        sortFields: 'createDate',
        sortDirections: sortDirection.DESC,
        pageSize: 50,
        pageIndex: 0,
        pageCount: 100,
        searchValues: '',
      };
    }

    if (fromURL && !isEmpty(filtersFromURL)) {
      const nextFilters = cloneDeep(initialFilters);
      for (const key in filtersFromURL) {
        if (key in nextFilters) {
          nextFilters[key] = filtersFromURL[key];
        }
      }

      return nextFilters;
    }

    return initialFilters;
  });

  const [expanded, setExpanded] = useState({});

  const sorting = useMemo(() => {
    if (isDefine(filters.sortField)) {
      return [{ id: filters.sortField, desc: filters.sortDirection === 'DESC' }];
    }

    return [];
  }, [filters.sortField, filters.sortDirection]);

  const [selectedFlatRows, setSelectedFlatRows] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  //! sortBy -> DEPREACATED -> use for TableCommonV1
  const [sortBy, setSortBy] = useState([]);
  const [isSelectAllRecord, setSelectAllRecord] = useState(false);

  const increaseOffset = useCallback(() => {
    const nextFilters = cloneDeep(filters);
    if ('offset' in nextFilters && 'limit' in nextFilters) {
      nextFilters.offset += nextFilters.limit;
    }
    setFilters(nextFilters);
    return nextFilters;
  }, [filters]);

  //* Use in case page | pageSize
  const increasePage = useCallback(() => {
    const nextFilters = cloneDeep(filters);

    if ('page' in nextFilters && 'size' in nextFilters) {
      nextFilters.page = (nextFilters.page || 0) + 1;
    }

    setFilters(nextFilters);
    return nextFilters;
  }, [filters]);

  const handleChangeSelect = useCallback(
    (name = '') =>
      (value) => {
        setFilters((prevFilters) => ({
          ...prevFilters,
          [name]: value,
        }));
      },
    [setFilters]
  );

  const handleChangeDateFromTo = useCallback(
    (name = '') =>
      (from, to) => {
        setFilters((prev) => {
          const nextPrev = cloneDeep(prev);
          if (nextPrev[name]) {
            nextPrev[name].from = from;
            nextPrev[name].to = to;
          }

          return nextPrev;
        });
      },
    []
  );

  const toggleSort = useCallback(
    (id, { setToFilters } = { setToFilters: true }) => {
      return () => {
        let cloneTempSortBy = cloneDeep(sortBy);
        const nextFilters = cloneDeep(filters);
        const isSetSortFields =
          setToFilters &&
          isPlainObject(nextFilters) &&
          'sortFields' in nextFilters &&
          'sortDirections' in nextFilters;
        /**
         * State Sort
         * [] - no sorting
         * desc: true - sort by DESC
         * desc: false - sort by ASC
         */
        if (isEmpty(cloneTempSortBy)) {
          setSortBy([{ id, desc: true }]);
          if (isSetSortFields) {
            nextFilters.sortFields = id;
            nextFilters.sortDirections = sortDirection.DESC;
          }
        } else {
          const header = cloneTempSortBy[0];
          const { id: idColumn, desc } = header;
          if (idColumn === id) {
            if (desc) {
              setSortBy([{ id, desc: false }]);
              if (isSetSortFields) {
                nextFilters.sortFields = id;
                nextFilters.sortDirections = sortDirection.ASC;
              }
            } else {
              setSortBy([]);
              if (clearSortWhenDefault) {
                if (isSetSortFields) {
                  nextFilters.sortFields = '';
                  nextFilters.sortDirections = '';
                }
              } else {
                if (isSetSortFields) {
                  nextFilters.sortFields = 'createDate';
                  nextFilters.sortDirections = sortDirection.DESC;
                }
              }
            }
          } else {
            setSortBy([{ id, desc: true }]);
            if (isSetSortFields) {
              nextFilters.sortFields = id;
              nextFilters.sortDirections = sortDirection.DESC;
            }
          }
        }

        if ('offset' in nextFilters) {
          nextFilters.offset = 0;
        }

        setFilters(nextFilters);
      };
    },
    [sortBy, filters, clearSortWhenDefault]
  );

  const resetSelectedFlatRows = () => {
    setSelectedFlatRows([]);
  };

  const resetSort = () => {
    setSortBy([]);
  };

  const resetCheckSelectAllRecord = () => {
    setSelectAllRecord(false);
  };

  const handleSelectAllRecord = (checked) => {
    setSelectAllRecord(checked);
  };

  const handleChangePage = useCallback((page) => {
    // resetSelectedFlatRows();
    // resetCheckSelectAllRecord();
    console.log({ page });
    setFilters((prevFilters) => {
      const nextFilters = cloneDeep(prevFilters);
      if ('page' in nextFilters) {
        nextFilters.page = page;
      }

      if ('pageIndex' in nextFilters) {
        nextFilters.pageIndex = page;
      }

      return nextFilters;
    });
  }, []);

  const handleChangePageSize = useCallback((pageSize) => {
    // resetSelectedFlatRows();
    // resetCheckSelectAllRecord();
    setFilters((prevFilters) => {
      const nextFilters = cloneDeep(prevFilters);
      if ('pageSize' in nextFilters) {
        nextFilters.pageSize = pageSize;
      }

      if ('size' in nextFilters) {
        nextFilters.size = pageSize;
      }

      if ('page' in nextFilters) {
        nextFilters.page = 0;
      }

      if ('pageIndex' in nextFilters) {
        nextFilters.pageIndex = 0;
      }

      return nextFilters;
    });
  }, []);

  const handleChangeInput = useCallback(
    (field = '') =>
      (e) => {
        const value = e.target.value;
        timerInput.current.debounce(() => {
          setFilters((prev) => ({
            ...prev,
            [field]: value.trim(),
          }));
        }, 300);
      },
    [setFilters]
  );

  const handleSearch = useCallback((filters) => {
    if ('page' in filters) {
      setFilters(() => {
        return {
          ...filters,
          page: 0,
        };
      });

      return;
    }

    if ('offset' in filters) {
      setFilters(() => {
        return {
          ...filters,
          offset: 0,
        };
      });
      return;
    }

    setFilters(() => {
      return {
        ...filters,
      };
    });
  }, []);

  const putValueToURL = useCallback(
    (filters) => {
      history.replace({
        search: `${queryString.stringify({
          ...queryString.parse(window.location.search),
          ...filters,
        })}`,
      });
    },
    [history.replace]
  );

  const resetFilterToInitial = useCallback(
    (filtersArg, { mutateURL = false } = {}) => {
      setSelectAllRecord(false);
      setSortBy([]);
      setSelectedFlatRows([]);
      if (!isEmpty(initialFilters)) {
        setFilters({ ...initialFilters, ...filtersArg });
      }

      if (fromURL && !isEmpty(filtersFromURL)) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('filters');

        history.replace({
          pathname: window.location.pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : '',
        });
      }

      if (mutateURL) {
        putValueToURL('');
        const nextUrl = queryString.parse(window.location.search) || {};
        delete nextUrl.filters;

        history.replace({
          search: '',
        });
      }
    },
    [initialFilters, putValueToURL, filtersFromURL, fromURL]
  );

  const refetch = useCallback((filtersArg) => {
    if (filtersArg) {
      setFilters({ ...filtersArg });
      return;
    }
    setFilters((prev) => ({ ...prev }));
  }, []);

  //* Encode to JWT Token -> put to url
  const saveFiltersToURL = useCallback(
    (filters) => {
      if (isEqual(initialFilters, filters)) {
        return;
      }

      const token = FiltersService.encodeFilters(filters);
      history.replace({
        search: `${queryString.stringify({
          ...queryString.parse(window.location.search),
          filters: token,
        })}`,
      });
    },
    [history.replace, initialFilters]
  );

  const tempSortField = isPlainObject(filters) && 'sortField' in filters ? filters?.sortField : '';
  const tempSortDirection =
    isPlainObject(filters) && 'sortDirection' in filters ? filters?.sortDirection : '';
  const tempInitialSortField =
    isPlainObject(initialFilters) && 'sortField' in initialFilters ? initialFilters?.sortField : '';
  const tempInitialSortDirection =
    isPlainObject(initialFilters) && 'sortDirection' in initialFilters
      ? initialFilters?.sortDirection
      : '';

  const handleToggleSortV2 = useCallback(
    ({ sortField }) => {
      let nextSortField = sortField;
      const currentSortField = tempSortField;
      const currentSortDirection = tempSortDirection;

      if (nextSortField !== currentSortField) {
        setFilters((prev) => ({
          ...prev,
          sortField: sortField,
          sortDirection: 'DESC',
        }));
        return;
      }

      let nextSortDirection = '';
      if (currentSortDirection === 'DESC') {
        nextSortDirection = 'ASC';
      }

      if (currentSortDirection === 'ASC') {
        nextSortField = tempInitialSortField || '';
        nextSortDirection = tempInitialSortDirection || '';
      }

      setFilters((prev) => ({
        ...prev,
        sortField: nextSortField,
        sortDirection: nextSortDirection,
      }));
    },
    [tempSortField, tempSortDirection, tempInitialSortField, tempInitialSortDirection]
  );

  const handleSorting = useCallback((nextSorting) => {
    setFilters((prev) => {
      if ('sortField' in prev && 'sortDirection' in prev) {
        const nextFilters = cloneDeep(prev);
        if (isEmpty(nextSorting)) {
          nextFilters.sortField = '';
          nextFilters.sortDirection = '';
        } else {
          nextFilters.sortField = nextSorting[0]?.id;
          nextFilters.sortDirection = nextSorting[0]?.desc ? 'DESC' : 'ASC';
        }

        return nextFilters;
      }

      return prev;
    });
  }, []);

  return {
    filters: filters,
    selectedFlatRows,
    sortBy,
    isSelectAllRecord,

    setFilters,
    setSelectedFlatRows,
    setSortBy,
    handleChangeSelect,
    handleSelectAllRecord,
    handleChangePageSize,
    handleChangePage,
    toggleSort,

    resetFilterToInitial,
    resetSelectedFlatRows,
    resetSort,
    resetCheckSelectAllRecord,
    increaseOffset,
    increasePage,
    refetch,
    handleChangeInput,
    handleChangeDateFromTo,
    handleSearch,
    putValueToURL,
    saveFiltersToURL,
    setSelectAllRecord,
    handleToggleSortV2,

    rowSelection,
    setRowSelection,

    expanded,
    setExpanded,

    sorting,
    handleSorting,

    hasFiltersFromURL: !isEmpty(filtersFromURL),
  };
};

export default useFilters;
